import { useNavigate, useParams } from "@tanstack/react-router";

import {
  usePlantProfile,
  useUpdatePlantProfile,
} from "@/lib/api/plant-profiles";
import { ApiError } from "@/lib/api/client";
import { ErrorState } from "@/components/error-state";
import { OptimisticLockBanner } from "@/components/optimistic-lock-banner";

import {
  PlantProfileForm,
  emptyToUndef,
  parseOptNum,
  type PlantProfileFormValues,
} from "./form";

export function PlantProfileEditPage() {
  const { plantProfileId } = useParams({
    from: "/chemistry-profile/plant-profiles/$plantProfileId/edit",
  });
  const navigate = useNavigate();
  const q = usePlantProfile(plantProfileId);
  const update = useUpdatePlantProfile(plantProfileId);

  if (q.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.error) return <ErrorState error={q.error} retry={q.refetch} />;
  if (!q.data) return null;
  if (q.data.system_provided) {
    return (
      <p className="text-sm text-muted-foreground">
        System plant profiles are read-only; create your own profile to
        customize.
      </p>
    );
  }

  function submit(values: PlantProfileFormValues) {
    if (!q.data) return;
    update.mutate(
      {
        expected_updated_at: q.data.updated_at,
        name: values.name,
        species: values.species,
        category: emptyToUndef(values.category),
        stage: emptyToUndef(values.stage),
        feeder_class: emptyToUndef(values.feeder_class),
        ph_min: parseOptNum(values.ph_min),
        ph_ideal: parseOptNum(values.ph_ideal),
        ph_max: parseOptNum(values.ph_max),
        ec_min: parseOptNum(values.ec_min),
        ec_ideal: parseOptNum(values.ec_ideal),
        ec_max: parseOptNum(values.ec_max),
        temp_min_c: parseOptNum(values.temp_min_c),
        temp_ideal_c: parseOptNum(values.temp_ideal_c),
        temp_max_c: parseOptNum(values.temp_max_c),
        notes: emptyToUndef(values.notes),
        reason: emptyToUndef(values.reason),
      },
      {
        onSuccess: () =>
          navigate({ to: "/chemistry-profile/plant-profiles" }),
      },
    );
  }

  const stale = update.error instanceof ApiError && update.error.isStale;
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">
        Edit plant profile
      </h1>
      <PlantProfileForm
        initial={q.data}
        submitting={update.isPending}
        showReason
        submitLabel="Save changes"
        onSubmit={submit}
        banner={
          stale ? (
            <OptimisticLockBanner
              error={update.error}
              onRefresh={() => {
                update.reset();
                void q.refetch();
              }}
            />
          ) : undefined
        }
      />
      {update.error && !stale && (
        <ErrorState error={update.error} retry={() => update.reset()} />
      )}
    </div>
  );
}
